import React, { useState, useEffect } from 'react';
import { FaUser, FaProjectDiagram, FaListAlt,  FaRulerCombined,  FaInfoCircle } from 'react-icons/fa';
import Dashboard from '../Admin/Dashboard';
import Weather from '../Admin/Weather';
import IAProfiling from '../Admin/ProfileSlide';
import IATraining from '../Admin/TrainingSlide';
import styled from 'styled-components';
import bg1 from '../Assets/bg/bg5.jpg';
import bg2 from '../Assets/bg/Pilar.jpg';
import bg3 from '../Assets/bg/SanMiguel.jpg';
import bg4 from '../Assets/bg/Capayas.jpg';
import bg5 from '../Assets/bg/Ubay.jpg';
import useNISCounts from '../Hooks/NisHooks/useCountsNIS';
import useCisBoholCounts from '../Hooks/CisHooks/useCountsCisBohol';
import useCisSiquijorCounts from '../Hooks/CisHooks/useCountsCisSiquijor';
import useCisNosoCounts from '../Hooks/CisHooks/useCountsCisNoso';
import FarmerStatus from '../Admin/FarmerStatus';
import FarmerList from '../Admin/FarmerList';
import FarmerDetails from '../Admin/FarmerDetails';
import { axiosApi } from '../api/setupApi';

const REFRESH_INTERVAL_MS = 5000;
const LAND_KEYS = ['farmArea', 'farm_area', 'serviceArea', 'service_area', 'irrigated_area', 'operational_area', 'contractArea', 'area', 'landArea', 'fusa'];
const CONVERGENCE_ENDPOINTS = ['/convergence/byType', '/convergence/list', '/convergence/getAlltype'];

const normalizeRows = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    if (payload.length === 1 && Array.isArray(payload[0])) {
      return payload[0];
    }
    return payload
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .filter((item) => item && typeof item === 'object');
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;

  if (payload?.success && payload?.data && typeof payload.data === 'object') {
    return Object.values(payload.data).flatMap((item) => (Array.isArray(item) ? item : []));
  }

  return [];
};

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    const direct = Number(cleaned);
    if (Number.isFinite(direct)) return direct;
    const firstNumeric = cleaned.match(/-?\d+(?:\.\d+)?/);
    return firstNumeric ? Number(firstNumeric[0]) : 0;
  }
  return 0;
};

const getLatestNisYear = (yearsPayload) => {
  const candidates = normalizeRows(yearsPayload)
    .flatMap((item) => [item?.years, item?.year, item?.value])
    .map((val) => Number(val))
    .filter((val) => Number.isFinite(val));

  if (!candidates.length) {
    return new Date().getFullYear();
  }

  return Math.max(...candidates);
};

const flattenNisMasterlistRows = (payload) => {
  if (!Array.isArray(payload)) return [];

  return payload.flatMap((isItem) =>
    (isItem?.ias || []).flatMap((ia) =>
      (ia?.rows || []).map((row) => ({ ...row, ia_name: ia?.ia_name, is_name: isItem?.is_name }))
    )
  );
};

const uniqueFarmerCount = (rows) => {
  const set = new Set();

  rows.forEach((row) => {
    const idValue = [row?.id, row?.farmerId, row?.ffrs_no, row?.ffrsNo, row?.rsbsa_no, row?.nationalId]
      .find((value) => value !== null && value !== undefined && String(value).trim() !== '');

    if (idValue !== undefined) {
      set.add(`id:${String(idValue).trim()}`);
      return;
    }

    const nameKey = [row?.lastName || row?.lastname, row?.firstName || row?.firstname, row?.middleName || row?.middlename]
      .map((part) => String(part || '').trim().toLowerCase())
      .join('|');

    if (nameKey.replace(/\|/g, '')) {
      set.add(`name:${nameKey}`);
    }
  });

  return set.size;
};

const rowLandArea = (row) => {
  const preferred = LAND_KEYS.reduce((amount, key) => {
    if (amount > 0) return amount;
    return toNumber(row?.[key]);
  }, 0);

  if (preferred > 0) return preferred;

  const fallbackKey = Object.keys(row || {}).find((key) => /area/i.test(key) && toNumber(row?.[key]) > 0);
  return fallbackKey ? toNumber(row[fallbackKey]) : 0;
};

const sumLandArea = (rows) => rows.reduce((sum, row) => sum + rowLandArea(row), 0);

const sumConvergenceArea = (rows) => rows.reduce((sum, row) => {
  const indicator = String(row?.indicator || '').toLowerCase();
  if (!indicator.includes('area')) return sum;
  return sum + toNumber(row?.total || row?.status_date || row?.statusToDate);
}, 0);

const fetchConvergenceRows = async () => {
  for (const endpoint of CONVERGENCE_ENDPOINTS) {
    try {
      const { data } = await axiosApi.get(endpoint);
      const rows = normalizeRows(data);
      if (rows.length) return rows;
    } catch (error) {
      const status = error?.response?.status;
      if (status && ![400, 404, 405].includes(status)) {
        throw error;
      }
    }
  }

  return [];
};

const buildProjectCount = (imtRows, convergenceRows) => {
  const uniqueProjects = new Set();

  imtRows.forEach((row) => {
    const key = row?.projectName || row?.name || row?.title || row?.id;
    if (key !== null && key !== undefined && String(key).trim() !== '') {
      uniqueProjects.add(String(key).trim().toLowerCase());
    }
  });

  convergenceRows.forEach((row) => {
    const key = row?.program || row?.projectName || row?.name || row?.id;
    if (key !== null && key !== undefined && String(key).trim() !== '') {
      uniqueProjects.add(String(key).trim().toLowerCase());
    }
  });

  return uniqueProjects.size;
};

const HomeAdmin = ({ darkMode, isSidebarExpanded = false }) => {
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [showIAProfiling, setShowIAProfiling] = useState(false);
  const [showIATraining, setShowIATraining] = useState(false);
  const [showIMTContract, setShowIMTContract] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoTitle, setInfoTitle] = useState('');
  const [infoDescription, setInfoDescription] = useState('');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [dashboardCounts, setDashboardCounts] = useState({
    farmers: 0,
    projects: 0,
    masterlist: 0,
    landArea: 0,
  });


  const handleSelectFarmer = (farmer) => {
    setSelectedFarmer(farmer);
  };

  const { counts: cisBoholCounts} = useCisBoholCounts();
  const { counts: cisSiquijorCounts} = useCisSiquijorCounts();
  const { counts: cisNosoCounts} = useCisNosoCounts();
  const { counts: nisCounts} = useNISCounts();

  const images = [bg1, bg2, bg3, bg4, bg5];

  const profileData = [
    { name: 'CIS Bohol', value: cisBoholCounts.cis_bohol },
    { name: 'CIS Siquijor', value: cisSiquijorCounts.cis_siquijor },
    { name: 'CIS Noso', value: cisNosoCounts.cis_noso },
    { name: 'NIS', value: nisCounts.nis },
    
  ];
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      setBackgroundIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [images.length]);

  useEffect(() => {
    let mounted = true;

    const loadCounts = async () => {
      try {
        const [fbMasterlistResult, imtResult, convergenceResult, yearsResult] = await Promise.allSettled([
          axiosApi.get('/fbMasterlist/getAll'),
          axiosApi.get('/imtContract/getAll'),
          fetchConvergenceRows(),
          axiosApi.get('/nis/years'),
        ]);

        const fbMasterlistPayload = fbMasterlistResult.status === 'fulfilled' ? fbMasterlistResult.value?.data : [];
        const imtPayload = imtResult.status === 'fulfilled' ? imtResult.value?.data : [];
        const convergenceRows = convergenceResult.status === 'fulfilled' ? convergenceResult.value : [];
        const yearsPayload = yearsResult.status === 'fulfilled' ? yearsResult.value?.data : [];

        const latestYear = getLatestNisYear(yearsPayload);
        let nisMasterlistPayload = [];

        try {
          const { data } = await axiosApi.get(`/nisMasterlist/nested/${latestYear}`);
          nisMasterlistPayload = data;
        } catch (error) {
          console.warn('Failed loading NIS masterlist count source:', error);
        }

        const fbRows = normalizeRows(fbMasterlistPayload);
        const nisRows = flattenNisMasterlistRows(nisMasterlistPayload);
        const imtRows = normalizeRows(imtPayload);
        const allMasterlistRows = [...fbRows, ...nisRows];

        if (!mounted) return;

        setDashboardCounts({
          farmers: uniqueFarmerCount(allMasterlistRows),
          projects: buildProjectCount(imtRows, convergenceRows),
          masterlist: allMasterlistRows.length,
          landArea: sumLandArea(allMasterlistRows) + sumLandArea(imtRows) + sumConvergenceArea(convergenceRows),
        });
      } catch (error) {
        if (mounted) {
          console.error('Failed loading dashboard realtime counts:', error);
        }
      }
    };

    loadCounts();
    const timer = setInterval(loadCounts, REFRESH_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const getCardCount = (item) => {
    switch (item) {
      case 'Farmers':
        return dashboardCounts.farmers.toLocaleString();
      case 'Projects':
        return dashboardCounts.projects.toLocaleString();
      case 'Masterlist':
        return dashboardCounts.masterlist.toLocaleString();
      case 'Land Area':
        return dashboardCounts.landArea.toLocaleString(undefined, { maximumFractionDigits: 2 });
      default:
        return '0';
    }
  };

  const handleBarClick = (data) => {
 
    setShowIAProfiling(false);
    setShowIATraining(false);
    setShowIMTContract(false);
  
    switch (data.name) {
      case 'IA Profile':
        setShowIAProfiling(true);
        break;
      case 'IA Training':
        setShowIATraining(true);
        break;
      case 'IMT Contract':
        setShowIMTContract(true);
        break;
      default:
        break;
    }
  };
  
  const getIcon = (item) => {
    switch (item) {
      case 'Farmers':
        return <FaUser color="#2e7d32" size={24} />;
      case 'Projects':
        return <FaProjectDiagram color="#1976d2" size={24} />;
      case 'Masterlist':
        return <FaListAlt color="#f57c00" size={24} />;
      case 'Land Area':
        return <FaRulerCombined color="#00838f" size={24} />;
      
      default:
        return null;
    }
  };
  
  const formItems = [
    'Farmers',
    'Projects',
    'Masterlist',
    'Land Area' 
  ];
  
  const handleInfoClick = (item) => {
    switch (item) {
      case 'Farmers':
        setInfoTitle('Farmers Count Explained');
        setInfoDescription('The Farmers count you see on your dashboard is the number of farmers ongoing for May. This reflects only those actively participating or registered for current initiatives.');
        break;
      case 'Projects':
        setInfoTitle('Projects Count Explained');
        setInfoDescription('Projects refers to ongoing or completed initiatives under the irrigation authority. It includes infrastructure, training, and support efforts.');
        break;
      case 'Masterlist':
        setInfoTitle('Masterlist Explained');
        setInfoDescription('Masterlist includes all registered farmers, their affiliations, and land data compiled by the system.');
        break;
      case 'Land Area':
        setInfoTitle('Land Area Explained');
        setInfoDescription('Land Area refers to the total irrigated or planned-to-irrigate hectares monitored under your IA jurisdiction.');
        break;
      default:
        setInfoTitle('');
        setInfoDescription('');
    }
    setShowInfoModal(true);
  };

  
  return (
  <Container isExpanded={isSidebarExpanded}>
      <FormsSection>
      {formItems.map((item, index) => (
  <FormCard key={index} onClick={() => console.log(`${item} clicked`)}>
  <LeftSection>
    <IconWrapper>
   {getIcon(item)}
    </IconWrapper>
    <div>
      <Label>{item}</Label>
      <Count>{getCardCount(item)}</Count>
    </div>
  </LeftSection>

  <InfoIcon onClick={(e) => {
   e.stopPropagation();
   handleInfoClick(item);
    }} />
  </FormCard>

    ))}

  </FormsSection>
  <ContentWrapper isExpanded={isSidebarExpanded}>
     

  <MainSection>
      <Dashboard profileData={profileData} bgImage={images[backgroundIndex]} onBarClick={handleBarClick} />
     
    <FarmerSection>
        <FarmerStatus />
        <FarmerDetails  farmer={selectedFarmer} darkMode={darkMode}/>
    </FarmerSection>
        

  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    <WeatherWrapper>
      <Weather />
    </WeatherWrapper>

    <div style={{ marginTop: '10px' }}>
    <FarmerList onSelectFarmer={handleSelectFarmer} />
    
    </div>
  </div>
   
  </MainSection>
      
  </ContentWrapper>


      {showIAProfiling && <IAProfiling setShowIAProfiling={setShowIAProfiling} />}
      {showIATraining && <IATraining setShowIATraining={setShowIATraining} />}
      {showIMTContract && <div>IMT Component Placeholder</div>} 

      {showInfoModal && (
    <ModalOverlay onClick={() => setShowInfoModal(false)}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h2>{infoTitle}</h2>
        <p>{infoDescription}</p>
        <CloseButton onClick={() => setShowInfoModal(false)}>OK</CloseButton>
      </ModalContent>
    </ModalOverlay>
  )}

    </Container>
  );
};

export default HomeAdmin;

const Container = styled.div`
  display: flex;
  margin-left: ${({ isExpanded }) => (isExpanded ? '24px' : '8px')};
  flex-direction: column;
  min-height: 100vh;
  padding-top: 70px;
  overflow-y: auto;
  background: linear-gradient(to right, #f4f6f8, #ffffff);
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  
  gap: 24px;
  padding: 10px;
  margin-top: -45px;
  margin-left:  0px;
`;

const MainSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr;
  gap: 25px; 
  width: 100%;

  
   @media (max-width: 120px) {
    grid-template-columns: 1fr; 
  }
`;

const WeatherWrapper = styled.div`
  width: 300px;   /* Fixed width for the Weather component */
  min-width: 250px; /* Ensures the weather widget doesn't shrink too much */
`;

const FarmerSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 20px;
`;


const FormsSection = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  padding: 10px 20px;
  margin: 60px 0;
  margin-top: -30px;
`;

const FormCard = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 280px;
  height: 60px;
  padding: 0 20px;
  background: linear-gradient(135deg, #ffffff, #f1f8e9);
  border-radius: 16px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(0,0,0,0.8);
              -8px -8px 20px rgba(255,255,255,0.8);
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 5px solid #2e7d32;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 10px 10px 25px rgba(0,0,0,0.12);
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Label = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #555;
`;

const Count = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2e7d32;
`;

const InfoIcon = styled(FaInfoCircle)`
  position: absolute;
  top: 8px;
  right: 8px;
  color: #999;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    color: #000;
  }
`;


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
 background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};
  padding: 10px;
  color: #333;
  border-radius: 15px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  animation: fadeInModal 0.3s ease;

  @keyframes fadeInModal {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const CloseButton = styled.button`
  margin-top: 20px;
  background: #4caf50;
  border: none;
  color: white;
  padding: 10px 25px;
  font-size: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #388e3c;
  }
`;
const IconWrapper = styled.div`
  width: 55px;
  height: 55px;
  border-radius: 50%;
  background: rgba(46, 125, 50, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
`;


